import groupBy from 'lodash/groupBy';
import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import { sortPortfolioDataPoints } from '@products/portfolio/portfolio.utils';
import {
  PortfolioAssetAllocationDTO,
  PortfolioComponentId,
  PortfolioDTO,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { Mapper, MapperParams } from '../type.mapper';
import { Logger } from '@utils/logger';
import { getFirstNonEmptyProp, parseStdNumber, parseYN } from '../mapper.utils';

const logger = Logger.getLogger('AssetAllocationMapper');

export const isAssetClassExposure = (
  point: PortfolioAssetAllocationDTO
): boolean =>
  point.assetalloccatgstd?.endsWith('_NET') ||
  point.assetalloccatgstd?.endsWith('_GROSS');

export const isAssetAllocation = (
  point: PortfolioAssetAllocationDTO
): boolean => !isAssetClassExposure(point);

// tslint:disable-next-line:max-line-length
// https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/18120/A/franklin-k-2-alternative-strategies-fund/FAAAX#portfolio
export const mapAssetClassExposure = (
  data: PortfolioAssetAllocationDTO[]
): PortfolioAllocation => {
  // this trims _NET or _GROSS off the end of the std category, so it can be grouped appropriately
  const grouper = (point: PortfolioAssetAllocationDTO): string =>
    point.assetalloccatgstd?.replace(/_(NET|GROSS)$/g, '');

  const allocation: PortfolioAllocation = {
    componentId: PortfolioComponentId.ASSET_CLASS_EXPOSURE,
    baseLabelKey: 'asset-class-exposure',
    showExposureChart: true,
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calctypelocal'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    data: Object.values(groupBy(data, grouper))
      // after grouping, we'll get an array with [gross, net] pairs e.g. [[{gross}, {net}], [{net}, {gross}], [{gross}, {net}], ...]
      .map(
        (
          group: PortfolioAssetAllocationDTO[]
        ): PortfolioAllocationDataPoint => {
          // extract the gross and net data points for this category
          const netPoint: PortfolioAssetAllocationDTO = group.find(
            (point: PortfolioAssetAllocationDTO): boolean =>
              point.assetalloccatgstd?.endsWith('_NET')
          );
          const grossPoint: PortfolioAssetAllocationDTO = group.find(
            (point: PortfolioAssetAllocationDTO): boolean =>
              point.assetalloccatgstd?.endsWith('_GROSS')
          );
          // return a single point with gross+net values
          return {
            label: group[0].assetalloccatg,
            grossExposure: formatPercentage(grossPoint?.brkdwnpct),
            grossExposureStd: parseStdNumber(grossPoint?.brkdwnpctstd),
            netExposure: formatPercentage(netPoint?.brkdwnpct),
            netExposureStd: parseStdNumber(netPoint?.brkdwnpctstd),
            // work out this point's order based on the lowest "rank" property of the category
            order: Math.min(
              parseStdNumber(grossPoint?.rank),
              parseStdNumber(netPoint?.rank)
            ),
          };
        }
      )
      // sort the array
      .sort(
        (
          a: PortfolioAllocationDataPoint,
          b: PortfolioAllocationDataPoint
        ): number => a.order - b.order
      ),
  };
  return allocation;
};

/**
 * example fund:
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
 */
export const mapAssetAllocation = (
  data: PortfolioAssetAllocationDTO[]
): PortfolioAllocation => ({
  componentId: PortfolioComponentId.ASSET_ALLOCATION,
  baseLabelKey: 'asset-allocation',
  allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
  allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
  calculationType: getFirstNonEmptyProp(data, 'calctypelocal'),
  hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: PortfolioAssetAllocationDTO): PortfolioAllocationDataPoint => ({
      label: obj.assetalloccatg,
      breakdown: formatPercentage(obj.brkdwnpct),
      breakdownStd: parseStdNumber(obj.brkdwnpctstd),
    })
  ),
});

export class AssetAllocationMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioAssetAllocationDTO[] = portfolioDto.assetallocation;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const allocations: PortfolioAllocation[] = [];

    const assetClassExposureData: PortfolioAssetAllocationDTO[] = data.filter(
      isAssetClassExposure
    );
    if (assetClassExposureData.length > 0) {
      allocations.push(mapAssetClassExposure(assetClassExposureData));
    }

    const assetAllocationData: PortfolioAssetAllocationDTO[] = data.filter(
      isAssetAllocation
    );
    if (assetAllocationData.length > 0) {
      allocations.push(mapAssetAllocation(assetAllocationData));
    }

    return allocations;
  }
}
